import * as THREE from "three";
import { FpsMeter } from "./fps";
import { InputManager } from "./input";
import { PASTEL_CLEAR, PASTEL_MIDGROUND } from "./pastel";
import { Player } from "./player";
import { APP_TITLE, APP_VERSION, PHASE_LABEL } from "./version";
import { World } from "./world";
import {
  CHUNK_SIZE,
  SEA_LEVEL,
  formatSeed,
  parseSeedString,
} from "./chunk";

/** Default golden seed — shareable baseline for playtests. */
const DEFAULT_SEED = 20260818;

function readSeedFromUrl(): number {
  try {
    const q = new URLSearchParams(window.location.search).get("seed");
    if (q != null && q !== "") return parseSeedString(q);
  } catch {
    /* ignore */
  }
  return DEFAULT_SEED;
}

function writeSeedToUrl(seed: number): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("seed", formatSeed(seed));
    window.history.replaceState({}, "", url.toString());
  } catch {
    /* ignore */
  }
}

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) {
  throw new Error("#app root missing");
}

const versionEl = document.querySelector<HTMLElement>("#version");
const fpsEl = document.querySelector<HTMLElement>("#fps");
const phaseEl = document.querySelector<HTMLElement>("#phase");
const coordsEl = document.querySelector<HTMLElement>("#coords");
const helpEl = document.querySelector<HTMLElement>("#help");
const seedInput = document.querySelector<HTMLInputElement>("#seed-input");
const seedApply = document.querySelector<HTMLButtonElement>("#seed-apply");
if (versionEl) versionEl.textContent = APP_VERSION;
if (phaseEl) phaseEl.textContent = PHASE_LABEL;
document.title = APP_TITLE;
const hudTitle = document.querySelector<HTMLElement>("#hud .title");
if (hudTitle) hudTitle.textContent = APP_TITLE;

const scene = new THREE.Scene();
const clearColor = new THREE.Color(PASTEL_CLEAR.r, PASTEL_CLEAR.g, PASTEL_CLEAR.b);
scene.background = clearColor;
// No fog — cozy clear sky (playtest 1a.2: near washout / gray milk). Pop-in
// softened by budgeted streaming + larger force-gen ring, not distance fog.
// Playtest 2.14: sky-blue clear + green mid-ground band above horizon
// (distant-land illusion). Not distance fog.

/**
 * Draw distance in chunks.
 * Phase 2.5: target ~35 (+5 from 2.18's 30) with grass LOD + Y-band mesher +
 * adaptive shrink under load — not brute VIEW=56 thrash.
 */
const VIEW_DISTANCE_TARGET = 35;
const VIEW_DISTANCE_MIN = 28; // floor when FPS is struggling (still ≥ 2.18)
let viewDistanceLive = VIEW_DISTANCE_TARGET;
const approxLoadRadiusBlocks = VIEW_DISTANCE_TARGET * CHUNK_SIZE;

const camera = new THREE.PerspectiveCamera(
  60,
  window.innerWidth / Math.max(1, window.innerHeight),
  0.5,
  Math.max(2400, approxLoadRadiusBlocks * 2.2),
);

const renderer = new THREE.WebGLRenderer({
  antialias: false, // cheaper; fine voxels + many chunks
  powerPreference: "high-performance",
  stencil: false,
  depth: true,
});
// Adaptive DPR: start conservative (Surface / iGPU), raise on stable high FPS
// (Alpha / dGPU). Fill-rate bound with many chunk meshes.
// Phase 2.5: start 0.70 / max 0.90 — LOD+Y-band carry outer cost; slightly
// roomier than 2.18 A-dials (0.65/0.85) while keeping Surface fill-rate in check.
const DPR_MIN = 0.55;
const DPR_MAX = 0.9;
const DPR_START = Math.min(window.devicePixelRatio || 1, 0.7);
let currentDpr = DPR_START;
renderer.setPixelRatio(currentDpr);
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.shadowMap.enabled = false;
// One light path is enough with Lambert materials
renderer.toneMapping = THREE.NoToneMapping;
app.appendChild(renderer.domElement);

// Soft cozy lighting (Lambert-friendly; no specular thrash)
const hemi = new THREE.HemisphereLight(0xfff0e8, 0xb8d4c8, 1.15);
scene.add(hemi);
const dir = new THREE.DirectionalLight(0xfff5e6, 0.55);
dir.position.set(40, 90, 30);
scene.add(dir);

/**
 * Sky dome + grass-green mid-ground band (playtest 2.15).
 * Large inward sphere follows the camera; gradient shader paints meadow green
 * just above the horizon, easing into sky blue overhead. Depth-write off so
 * world geometry always wins; fog stays off.
 */
function makeSkyDome(): THREE.Mesh {
  const skyGeo = new THREE.SphereGeometry(1800, 32, 16);
  // Flip normals so we see the inside of the sphere
  skyGeo.scale(-1, 1, 1);
  const skyMat = new THREE.ShaderMaterial({
    side: THREE.FrontSide,
    depthWrite: false,
    depthTest: false,
    fog: false,
    uniforms: {
      topColor: {
        value: new THREE.Color(PASTEL_CLEAR.r, PASTEL_CLEAR.g, PASTEL_CLEAR.b),
      },
      midColor: {
        value: new THREE.Color(
          PASTEL_MIDGROUND.r,
          PASTEL_MIDGROUND.g,
          PASTEL_MIDGROUND.b,
        ),
      },
      // Normalized Y bands on the unit sphere direction
      // horizonY: where green starts above geometric horizon
      // midPeakY: strongest green
      // skyY: fully sky-blue above this
      horizonY: { value: -0.02 },
      // 2.15: peak green 20% lower above horizon (0.06 → 0.048)
      midPeakY: { value: 0.048 },
      skyY: { value: 0.24 },
    },
    vertexShader: /* glsl */ `
      varying vec3 vDir;
      void main() {
        // Object-space position ≈ direction once sphere is camera-centered
        vDir = normalize(position);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 topColor;
      uniform vec3 midColor;
      uniform float horizonY;
      uniform float midPeakY;
      uniform float skyY;
      varying vec3 vDir;
      void main() {
        float y = vDir.y;
        vec3 col;
        if (y <= horizonY) {
          // Below geometric horizon: deepen slightly toward mid green
          // (rarely visible with ground, but keeps dome continuous)
          col = mix(midColor * 0.85, midColor, smoothstep(-0.35, horizonY, y));
        } else if (y < midPeakY) {
          float t = smoothstep(horizonY, midPeakY, y);
          col = mix(midColor, midColor, t);
        } else if (y < skyY) {
          float t = smoothstep(midPeakY, skyY, y);
          col = mix(midColor, topColor, t);
        } else {
          // Upper sky: slight brighten toward zenith
          float t = smoothstep(skyY, 1.0, y);
          col = mix(topColor, topColor * 1.06, t * 0.5);
        }
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  });
  const mesh = new THREE.Mesh(skyGeo, skyMat);
  mesh.frustumCulled = false;
  mesh.renderOrder = -1000;
  mesh.name = "sky-dome";
  return mesh;
}
const skyDome = makeSkyDome();
scene.add(skyDome);

let activeSeed = readSeedFromUrl();
writeSeedToUrl(activeSeed);
if (seedInput) seedInput.value = formatSeed(activeSeed);

/** Cold-start resident ring (Chebyshev). ~r5 = 11×11 = 121 columns. */
const LOAD_RING_R = 5;

const world = new World({
  viewDistance: VIEW_DISTANCE_TARGET,
  // Phase 2.5: gen/remesh lean; grass LOD + Y-band carry outer draw cost.
  remeshBudget: 8,
  genBudget: 5,
  // Keep force ring inside resident preload so post-load stream is gentle.
  forceGenRadius: Math.min(3, LOAD_RING_R),
  streamTimeBudgetMs: 6,
  lookAheadChunks: 10,
  nearDetailRadius: 10, // full tall-grass blades
  farGrassRadius: 18, // beyond → no grass faces
  worldgen: { seed: activeSeed, mode: "noise" },
});
scene.add(world.group);

const player = new Player(world);

const loadOverlay = document.getElementById("loading-overlay");
const loadFill = document.getElementById("loading-fill") as HTMLElement | null;
const loadPct = document.getElementById("loading-pct");
const loadMsg = document.getElementById("loading-msg");
let worldReady = false;

function setLoadProgress(p: number, msg?: string): void {
  const pct = Math.max(0, Math.min(100, Math.round(p * 100)));
  if (loadFill) loadFill.style.width = `${pct}%`;
  if (loadPct) loadPct.textContent = `${pct}%`;
  if (msg && loadMsg) loadMsg.textContent = msg;
}

/**
 * One-time cold-start: sync-generate + mesh the resident ring, then ungate.
 * Yields so the loading bar can paint before/after the heavy work.
 */
async function coldStartPreload(): Promise<void> {
  setLoadProgress(0.05, "Laying bedrock…");
  await new Promise<void>((r) => requestAnimationFrame(() => r()));
  const rad = LOAD_RING_R;
  world.preloadRing(0, 0, rad);
  setLoadProgress(0.88, "Meshing the wilds…");
  await new Promise<void>((r) => requestAnimationFrame(() => r()));
  // Extra stream ticks for neighbor seam quality / dirties.
  for (let i = 0; i < 8; i++) {
    world.updateAround(0, 0, 0, 0);
  }
  setLoadProgress(0.96, "Finding your footing…");
  player.spawnAt(0, 0);
  scene.add(player.mesh);
  const ready = world.isRingReady(0, 0, rad);
  setLoadProgress(1, ready ? "Hearth warm" : "Almost…");
  await new Promise<void>((r) => requestAnimationFrame(() => r()));
  if (loadOverlay) {
    loadOverlay.classList.add("hidden");
    loadOverlay.setAttribute("aria-busy", "false");
    window.setTimeout(() => loadOverlay.remove(), 400);
  }
  worldReady = true;
}

// Kick cold-start immediately (overlay visible until done).
void coldStartPreload();

const input = new InputManager(renderer.domElement);

if (helpEl) {
  helpEl.textContent =
    "Click to capture · WASD move · Space jump · Shift sprint · mouse look while moving (3rd person) · seed in HUD or ?seed=";
}

function applySeed(raw: string): void {
  const next = parseSeedString(raw);
  if (next === activeSeed) {
    writeSeedToUrl(next);
    if (seedInput) seedInput.value = formatSeed(next);
    return;
  }
  activeSeed = next;
  writeSeedToUrl(next);
  if (seedInput) seedInput.value = formatSeed(next);
  world.setWorldgen({ seed: next, mode: "noise" });
  for (let i = 0; i < 20; i++) {
    world.updateAround(0, 0, 0, 0);
  }
  player.spawnAt(0, 0);
  console.info(`[fthop] seed → ${formatSeed(next)}`);
}

if (seedApply && seedInput) {
  seedApply.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    applySeed(seedInput.value);
  });
  seedInput.addEventListener("keydown", (e) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      applySeed(seedInput.value);
    }
  });
  // Don't steal WASD while typing seed.
  seedInput.addEventListener("pointerdown", (e) => e.stopPropagation());
}

const fpsMeter = new FpsMeter(45);
let lastHud = 0;
let lastT = performance.now();
let dprCooldown = 0;
let viewCooldown = 0;

function applyDpr(next: number): void {
  const clamped = Math.max(DPR_MIN, Math.min(DPR_MAX, next));
  if (Math.abs(clamped - currentDpr) < 0.04) return;
  currentDpr = clamped;
  renderer.setPixelRatio(currentDpr);
  renderer.setSize(window.innerWidth, window.innerHeight, false);
}

function applyViewDistance(next: number): void {
  const clamped = Math.max(
    VIEW_DISTANCE_MIN,
    Math.min(VIEW_DISTANCE_TARGET, Math.round(next)),
  );
  if (clamped === viewDistanceLive) return;
  viewDistanceLive = clamped;
  world.setViewDistance(clamped);
  // Keep far plane honest when adaptive view moves
  const farBlocks = Math.max(2400, clamped * CHUNK_SIZE * 2.2);
  if (Math.abs(camera.far - farBlocks) > 32) {
    camera.far = farBlocks;
    camera.updateProjectionMatrix();
  }
}

function onResize(): void {
  const w = window.innerWidth;
  const h = Math.max(1, window.innerHeight);
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  renderer.setSize(w, h);
}
window.addEventListener("resize", onResize);

function frame(now: number): void {
  requestAnimationFrame(frame);
  const dt = Math.min(0.05, Math.max(0, (now - lastT) / 1000));
  lastT = now;

  // Hold gameplay until cold-start ring is ready (one-time loading gate).
  if (!worldReady) {
    skyDome.position.copy(camera.position);
    renderer.render(scene, camera);
    return;
  }

  // Look first so camera yaw updates even if world work runs long this frame.
  player.update(dt, input);
  player.applyCamera(camera);
  // Sky dome stays centered on camera so the mid-ground band sits on the horizon.
  skyDome.position.copy(camera.position);
  // Stream after movement so focus follows new feet; pass velocity so the
  // heading disk preloads the edge you walk into (playtest 2.8).
  world.updateAround(player.x, player.z, player.vx, player.vz);

  const fps = fpsMeter.tick(now);
  dprCooldown -= dt;
  viewCooldown -= dt;
  if (dprCooldown <= 0 && Number.isFinite(fps) && fps > 0) {
    // Surface (low FPS): drop DPR. Alpha (stable high): gently raise toward max.
    if (fps < 28) {
      applyDpr(currentDpr - 0.12);
      dprCooldown = 0.55;
    } else if (fps < 40) {
      applyDpr(currentDpr - 0.05);
      dprCooldown = 0.85;
    } else if (fps > 54 && currentDpr < DPR_MAX) {
      applyDpr(currentDpr + 0.06);
      dprCooldown = 1.2;
    } else {
      dprCooldown = 0.4;
    }
  }
  // Phase 2.5: adaptive view — shrink under load, restore toward target when healthy.
  // Slower than DPR so we don't thrash chunk load/unload every half-second.
  if (viewCooldown <= 0 && Number.isFinite(fps) && fps > 0) {
    if (fps < 24 && viewDistanceLive > VIEW_DISTANCE_MIN) {
      applyViewDistance(viewDistanceLive - 2);
      viewCooldown = 1.4;
    } else if (fps < 30 && viewDistanceLive > VIEW_DISTANCE_MIN) {
      applyViewDistance(viewDistanceLive - 1);
      viewCooldown = 1.8;
    } else if (fps > 48 && viewDistanceLive < VIEW_DISTANCE_TARGET) {
      applyViewDistance(viewDistanceLive + 1);
      viewCooldown = 2.2;
    } else {
      viewCooldown = 0.6;
    }
  }

  if (now - lastHud > 200) {
    if (fpsEl) fpsEl.textContent = fpsMeter.display;
    // Cartesian HUD: X east, Y up, Z south; origin xz=(0,0) = spawn / future village
    if (coordsEl) {
      // 0.2.54: X/Z as chunk tiles of 32 (ceil(|pos|/32) with sign); Y stays world meters
      const chunkTile = (v: number) => {
        const t = Math.ceil(Math.abs(v) / 32);
        return v < 0 ? -t : t;
      };
      coordsEl.textContent = `x ${chunkTile(player.x)}  y ${player.y.toFixed(1)}  z ${chunkTile(player.z)}`;
    }
    lastHud = now;
  }

  renderer.render(scene, camera);
}

requestAnimationFrame(frame);

console.info(
  `[fthop] v${APP_VERSION} phase-${PHASE_LABEL} | seed=${formatSeed(activeSeed)} | sea=${SEA_LEVEL} | webgl=${renderer.capabilities.isWebGL2 ? "2" : "1"} | chunks→${world.loadedChunkCount} | view=${viewDistanceLive}/${VIEW_DISTANCE_TARGET} | fog=off | dpr=${currentDpr.toFixed(2)} | lod=grass+yband`,
);
